import { Injectable } from '@angular/core';

export interface ThermostatData {
  heatPriorities: {
    solarEnabled: boolean;
    heatPumpEnabled: boolean;
    gasEnabled: boolean;
  };
  heatAvailabilities: {
    solar: 'available' | 'notAvailable';
    heatPump: 'available' | 'notAvailable';
    gas: 'available' | 'notAvailable';
  };
  thermostatMode: 'heat' | 'chill' | 'auto';
  status: {
    executionState: 'running' | 'waiting' | 'stopped' | 'paused' | 'standby';
    isBlockedByEquipment: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ThermostatSourceStatusService {

  // ---------- SOLAR ----------
  getSolarStatus(data: ThermostatData): string {
    const p = data.heatPriorities;
    const a = data.heatAvailabilities;
    const s = data.status;
    const mode = data.thermostatMode;

    // Disabled
    if (!p.solarEnabled) return 'Disabled';

    // Low Sun
    if (a.solar !== 'available') return 'Low Sun';

    // Heating
    if (
      mode === 'heat' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    ) {
      return 'Heating';
    }
    // Standby
    return 'Standby';
  }

  // ---------- HELPER: SOLAR ACTIVE ----------
  private isSolarActive(data: ThermostatData): boolean {
    const p = data.heatPriorities;
    const a = data.heatAvailabilities;
    const s = data.status;

    return (
      p.solarEnabled &&
      a.solar === 'available' &&
      data.thermostatMode === 'heat' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    );
  }

  // ---------- HEAT PUMP ----------
  getHeatPumpStatus(data: ThermostatData): string {
    const p = data.heatPriorities;
    const a = data.heatAvailabilities;
    const s = data.status;
    const mode = data.thermostatMode;

    // Disabled
    if (!p.heatPumpEnabled) return 'Disabled';

    // Low Air Temperature
    if (a.heatPump !== 'available') return 'Low Air Temperature';

    // Standby (Solar is active)
    if (this.isSolarActive(data)) {
      return 'Standby';
    }

    // Heating
    if (
      mode === 'heat' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    ) {
      return 'Heating';
    }

    // Chilling
    if (
      mode === 'chill' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    ) {
      return 'Chilling';
    }

    // Auto Mode
    if(
      mode === 'auto' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    ) {
      return 'Auto';
    }

    // Off
    return 'Off';
  }

  // ---------- HELPER: HEAT PUMP ACTIVE ----------
  private isHeatPumpActive(data: ThermostatData): boolean {
    const p = data.heatPriorities;
    const a = data.heatAvailabilities;
    const s = data.status;

    return (
      p.heatPumpEnabled &&
      a.heatPump === 'available' &&
      data.thermostatMode === 'heat' &&
      (s.executionState === 'running' || s.executionState === 'waiting') &&
      !this.isSolarActive(data) // only if solar NOT active
    );
  }

  // ---------- GAS ----------
  getGasStatus(data: ThermostatData): string {
    const p = data.heatPriorities;
    const s = data.status;
    const mode = data.thermostatMode;
    const a = data.heatAvailabilities;


    // Disabled
    if (!p.gasEnabled) return 'Disabled';

    // Unavailable
    if (a.gas !== 'available') return 'Standby';

    // Standby (Solar OR Heat Pump active)
    if (a.gas === 'available' && (this.isSolarActive(data) || this.isHeatPumpActive(data))) {
      return 'Standby';
    }

    // Heating
    if (
      mode === 'heat' &&
      (s.executionState === 'running' || s.executionState === 'waiting')
    ) {
      return 'Heating';
    }

    // Off
    return 'Off';
  }
}